/**
 * 토스트(일시 알림) 스토어.
 *
 * Owns:
 * - 토스트 큐와 각 토스트의 자동 닫힘 타이머(lifecycle).
 * - tone별 기본 표시 시간·최대 동시 노출 수 정책.
 * - hover/focus 동안 자동 닫힘을 멈췄다 재개하는 pause/resume(스택 전체). 호스트가 호출한다.
 * - UI와 분리된 외부 스토어(subscribe/getSnapshot) 노출 — 호스트 컴포넌트(`ToastHost`)뿐
 *   아니라 페이지·모달 핸들러처럼 컴포넌트 밖에서도 `toast.success(...)`로 띄울 수 있다.
 *
 * Does not own:
 * - 렌더링·배치·접근성 — 호스트 컴포넌트 `ToastHost` 가 소유한다.
 * - 도메인 로직 — 호출부(페이지·모달)가 결과를 받아 메시지를 만든다.
 */

export type ToastTone = "info" | "success" | "warning" | "error";

// 토스트에 붙는 보조 액션(예: "View review").
export type ToastAction = {
  label: string;
  onPress: () => void;
  // 누른 뒤 호스트가 토스트를 닫을지. 기본 true("페이지로 이동"처럼 떠나는 액션). false면 토스트가 남아
  // "재연결"처럼 결과를 같은 토스트에서 이어 보여줄 수 있다 — onPress 안에서 같은 id로 replace한다.
  dismissOnPress?: boolean;
};

export type Toast = {
  id: string;
  // 같은 id가 닫혔다 다시 뜰 때마다 증가하고 replace에서는 유지되는 번호. 호스트가 id 대신 이 쌍으로 카드를
  // 식별해, 한 이벤트에서 닫고 다시 띄워도 옛 카드는 제자리에서 퇴장하고 새 카드가 맨 뒤에서 등장한다.
  instance: number;
  // 같은 instance가 replace된 횟수. 호스트가 이 값이 오를 때마다 카드 안에서 갱신 큐를 재생한다.
  revision: number;
  tone: ToastTone;
  message: string;
  action: ToastAction | null;
};

export type ToastOptions = {
  // 자동 닫힘까지의 시간(ms). 생략하면 tone(과 action 유무)별 기본값, null 이면 자동으로 닫지 않는다.
  durationMs?: number | null;
  // 보조 액션. 있으면 사용자가 읽고 누를 시간을 위해 기본 표시 시간이 길어진다.
  action?: ToastAction;
  // 호출부가 정하는 id. 같은 id의 토스트가 살아 있으면 새로 추가하지 않고 그 자리에서 교체한다
  // (같은 알림의 중복 노출 방지·상태 전환용). 자동 발급 id와 겹치지 않게 `toast-` 접두는 피한다.
  id?: string;
};

// tone별 기본 표시 시간. 경고·실패는 더 오래 두어 사용자가 놓치지 않게 한다.
const defaultDurationByTone: Record<ToastTone, number> = {
  info: 4_000,
  success: 4_000,
  warning: 5_000,
  error: 6_000,
};

// 액션이 달린 토스트는 누를 시간을 주려고 더 오래 노출한다.
const actionDurationMs = 8_000;

// 화면을 가리지 않도록 동시에 노출하는 토스트 수를 제한한다. 초과하면 가장 오래된 것부터 치운다.
const maxVisibleToasts = 4;

// 토스트 1건의 자동 닫힘 타이머. hover/focus 일시정지를 위해 남은 시간을 추적한다.
type ToastTimer = {
  // 실행 중이면 setTimeout 핸들, 일시정지 중이면 null.
  handle: ReturnType<typeof setTimeout> | null;
  // 남은 표시 시간(ms). 일시정지할 때 경과분을 빼서 갱신한다.
  remainingMs: number;
  // 현재 구간이 시작된 시각(ms). handle 이 살아 있을 때만 의미가 있다.
  startedAt: number;
};

export class ToastManager {
  private readonly listeners = new Set<() => void>();
  private readonly timers = new Map<string, ToastTimer>();
  // hover/focus 동안 스택 전체의 자동 닫힘을 멈춘다. 멈춤 중 새로 뜬 토스트도 타이머를 걸지 않는다.
  private paused = false;
  private toasts: readonly Toast[] = [];
  // useSyncExternalStore 계약상 변화가 없으면 같은 참조를 돌려줘야 한다. 변경 시에만 교체한다.
  private snapshot: readonly Toast[] = this.toasts;
  // 매니저 내부에서만 유일하면 충분한 id. crypto 전역 의존을 피하려 단조 증가 카운터를 쓴다.
  private idCounter = 0;
  // 새로 추가되는 토스트마다 증가하는 instance 번호(replace는 기존 값을 잇는다).
  private instanceCounter = 0;

  getSnapshot = (): readonly Toast[] => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // 토스트를 띄우고 그 id를 반환한다. 자동 닫힘이 켜져 있으면 타이머를 건다.
  // 호출부 id가 살아 있는 토스트와 겹치면 스택 위치를 유지한 채 내용을 교체하고 타이머를 새로 시작한다.
  show = (message: string, tone: ToastTone = "info", options: ToastOptions = {}): string => {
    const id = options.id ?? `toast-${(this.idCounter += 1)}`;
    const action = options.action ?? null;

    const existing = this.toasts.find((toast) => toast.id === id);
    if (existing !== undefined) {
      const replaced: Toast = {
        id,
        instance: existing.instance,
        revision: existing.revision + 1,
        tone,
        message,
        action,
      };
      this.toasts = this.toasts.map((toast) => (toast.id === id ? replaced : toast));
      // 항목 수가 늘지 않으므로 최대 노출 수 정리는 필요 없다. 이전 타이머는 버리고 새 시간으로 다시 센다.
      this.clearTimer(id);
    } else {
      const toast: Toast = {
        id,
        instance: (this.instanceCounter += 1),
        revision: 0,
        tone,
        message,
        action,
      };
      let next = [...this.toasts, toast];
      // 최대 노출 수를 넘기면 가장 오래된 토스트부터 제거하고 그 타이머도 정리한다.
      while (next.length > maxVisibleToasts) {
        this.clearTimer(next[0].id);
        next = next.slice(1);
      }
      this.toasts = next;
    }

    const durationMs = resolveDurationMs(tone, action, options.durationMs);
    if (durationMs !== null) {
      this.startTimer(id, durationMs);
    }
    this.emit();
    return id;
  };

  info = (message: string, options?: ToastOptions): string => this.show(message, "info", options);

  success = (message: string, options?: ToastOptions): string =>
    this.show(message, "success", options);

  warning = (message: string, options?: ToastOptions): string =>
    this.show(message, "warning", options);

  error = (message: string, options?: ToastOptions): string => this.show(message, "error", options);

  // 토스트를 닫는다(자동 닫힘 타이머·수동 닫기·액션 클릭 공용). 없는 id는 무시한다.
  dismiss = (id: string): void => {
    if (!this.toasts.some((toast) => toast.id === id)) {
      return;
    }
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.clearTimer(id);
    this.emit();
  };

  // 자동 닫힘을 일시정지한다(hover/focus). 실행 중인 타이머를 멈추고 각 토스트의 남은 시간을 보관한다. 멱등.
  pause = (): void => {
    if (this.paused) {
      return;
    }
    this.paused = true;
    const now = Date.now();
    for (const timer of this.timers.values()) {
      if (timer.handle !== null) {
        clearTimeout(timer.handle);
        timer.remainingMs = Math.max(0, timer.remainingMs - (now - timer.startedAt));
        timer.handle = null;
      }
    }
  };

  // 일시정지를 풀고 각 토스트의 남은 시간으로 자동 닫힘을 재개한다. 멱등.
  resume = (): void => {
    if (!this.paused) {
      return;
    }
    this.paused = false;
    const now = Date.now();
    for (const [id, timer] of this.timers) {
      if (timer.handle === null) {
        timer.startedAt = now;
        timer.handle = setTimeout(() => this.dismiss(id), timer.remainingMs);
      }
    }
  };

  // 자동 닫힘 타이머를 건다. 스택이 일시정지 중이면 타이머는 걸지 않고 남은 시간만 보관해 두었다가
  // resume 시점에 시작한다(멈춤 중 도착한 토스트도 hover 가 풀릴 때까지 그대로 유지).
  private startTimer(id: string, durationMs: number): void {
    const timer: ToastTimer = { handle: null, remainingMs: durationMs, startedAt: Date.now() };
    if (!this.paused) {
      timer.handle = setTimeout(() => this.dismiss(id), durationMs);
    }
    this.timers.set(id, timer);
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      if (timer.handle !== null) {
        clearTimeout(timer.handle);
      }
      this.timers.delete(id);
    }
  }

  private emit(): void {
    this.snapshot = this.toasts;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// 표시 시간 결정: 명시 override 우선(null=고정), 없으면 액션 있는 토스트는 길게, 그 외 tone 기본값.
function resolveDurationMs(
  tone: ToastTone,
  action: ToastAction | null,
  override: number | null | undefined,
): number | null {
  if (override !== undefined) {
    return override;
  }
  if (action !== null) {
    return actionDurationMs;
  }
  return defaultDurationByTone[tone];
}

// 패키지 스코프 싱글턴. 모듈 1회 평가라 StrictMode 재마운트에도 동일 인스턴스를 유지한다.
export const toast = new ToastManager();
